import { combineLatest, map } from 'rxjs';
import { APIFieldAttributes, ArdoqId } from '@ardoq/api-types';
import { fields$, FieldsState } from 'streams/fields/fields$';
import { models$, ModelsState } from 'streams/models/models$';
import {
  persistentWorkspaces$ as workspaces$,
  WorkspacesState,
} from 'streams/workspaces/workspaces$';

export type WorkspaceExternallyManagedFields = {
  componentFieldNames: ReadonlySet<string>;
  referenceFieldNames: ReadonlySet<string>;
};

export type ExternallyManagedFieldNamesByWorkspaceId = Readonly<
  Record<ArdoqId, WorkspaceExternallyManagedFields>
>;

export type ReadonlyFieldData = {
  externallyManagedFieldNamesByWorkspaceId: ExternallyManagedFieldNamesByWorkspaceId;
  calculatedFieldNames: ReadonlySet<string>;
};

const toExternallyManagedFieldNamesByWorkspaceId = (
  workspaces: WorkspacesState
): Record<ArdoqId, WorkspaceExternallyManagedFields> =>
  Object.fromEntries(
    workspaces.models.map(workspace => {
      const integrations = workspace.integrations ?? [];
      return [
        workspace._id,
        {
          componentFieldNames: new Set(
            integrations.flatMap(integration => integration.componentFields)
          ),
          referenceFieldNames: new Set(
            integrations.flatMap(integration => integration.referenceFields)
          ),
        },
      ];
    })
  );

const modelIsTemplate = (models: ModelsState, modelId: ArdoqId): boolean => {
  const model = models.byId[modelId];
  if (!model) return false;
  return Boolean(model.common || model.useAsTemplate);
};

const fieldIsExcludedAsTemplate = (
  field: APIFieldAttributes,
  workspacesByModelId: Set<ArdoqId>,
  models: ModelsState
): boolean =>
  workspacesByModelId.has(field.model) && modelIsTemplate(models, field.model);

const toCalculatedFieldNames = (
  fields: FieldsState,
  workspaces: WorkspacesState,
  models: ModelsState
): Set<string> => {
  const workspacesByModelId = new Set(
    workspaces.models.map(workspace => workspace.componentModel)
  );
  const calculatedFieldNames = new Set<string>();
  for (const [fieldName, field] of fields.byName) {
    if (
      field.calculatedFieldSettings &&
      !fieldIsExcludedAsTemplate(field, workspacesByModelId, models)
    ) {
      calculatedFieldNames.add(fieldName);
    }
  }
  return calculatedFieldNames;
};

export const readonlyFieldData$ = combineLatest([
  workspaces$,
  fields$,
  models$,
]).pipe(
  map(([workspaces, fields, models]): ReadonlyFieldData => ({
    externallyManagedFieldNamesByWorkspaceId:
      toExternallyManagedFieldNamesByWorkspaceId(workspaces),
    calculatedFieldNames: toCalculatedFieldNames(fields, workspaces, models),
  }))
);
